import * as React from 'react';
// Import hooks.
import {
	UseInputFormResult,
	useInputPolicyNumber,
	UseInputPolicyNumberResults,
	useInputPostalCode,
	UseInputPostalCodeResults,
} from '@app/hooks/view/input';
// Import props.
import {
	FormProps,
	NavigationFooterProps,
	FormPublicPolicyViewPreliminaryProps,
	ScrollbarProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
} from '@app/enumerations';

interface UseFormPublicPolicyViewPreliminaryParameters {
	id: string;
	props: FormPublicPolicyViewPreliminaryProps,
	showValidations?: boolean;
}

interface UseFormPublicPolicyViewPreliminaryResults {
	submitForm: () => void;
	policyNumberInput: UseInputPolicyNumberResults;
	insuredPostalCodeInput: UseInputPostalCodeResults;
	applyFormProps: () => FormProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPublicPolicyViewPreliminary = (params: UseFormPublicPolicyViewPreliminaryParameters): UseFormPublicPolicyViewPreliminaryResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidationWithFocus();
		if (isValid) {
			params.props.onViewPublicPolicyView(policyNumberInput.value, insuredPostalCodeInput.value);		
		}
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			fixedNavigation: false,
			themeType: AppThemeColor.PRIMARY,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: true,
			leftButtonLabel: '',
			rightButtonDisabled: false,
			rightButtonLabel: 'View Policy',
			rightButtonClick: submitForm,
			alignment: NavigationFooterAlignment.SPACE_BETWEEN,
		};
	};

	/**
	 * Validates an individual input with focus.
	 * @param input
	 */
	const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
		let isValid = input.isInputValid;
		if (!isValid) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation with focus. 
	 **/
	const checkFormValidationWithFocus = (): boolean => {
		return checkInputValidationWithFocus<number>(policyNumberInput)
			&& checkInputValidationWithFocus<string>(insuredPostalCodeInput);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		policyNumberInput.changeShowValidationFlag(true);
		insuredPostalCodeInput.changeShowValidationFlag(true);
	}

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generates id for policy number. 
	 **/
	const generateIdForPolicyNumber = (): string => {
		return `${params.id}-policyNumber`;
	}

	/**
	 * Generates id for insured postal code.
	 **/
	const generateIdForInsuredPostalCode = (): string => {
		return `${params.id}-insuredPostalCode`;
	}

	// #endregion private methods


	// #region variables

	// Setup the policy number input hook.
	const policyNumberInput: UseInputPolicyNumberResults = useInputPolicyNumber({
		id: generateIdForPolicyNumber(),
		label: 'Policy Number',
		required: true,
		disabled: false,
		value: String.empty(),
		showValidation: params.showValidations,
	});

	// Setup the insured postal code input hook.
	const insuredPostalCodeInput: UseInputPostalCodeResults = useInputPostalCode({
		id: generateIdForInsuredPostalCode(),
		label: 'Policyholder/Insured\'s Property Address Zip Code',
		required: true,
		disabled: false,
		value: String.empty(),
		showValidation: params.showValidations,
	});

	// #endregion private variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!params.showValidations
			|| checkFormValidationWithFocus()) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			policyNumberInput.focusToInput();
		}
	}, []);

	// #endregion life-cycle methods.


	return {
		submitForm,
		policyNumberInput,
		insuredPostalCodeInput,
		applyFormProps,
		applyNavigationFooterProps,
	};
}

// Export parameters.
export {
	UseFormPublicPolicyViewPreliminaryParameters,
}

// Export results.
export {
	UseFormPublicPolicyViewPreliminaryResults,
}

export default useFormPublicPolicyViewPreliminary;